import { useState, useEffect, useCallback } from 'react';

// T038 — Theme switcher. Mirrors the pattern from the ishareinvest sister
// project: localStorage-persisted theme id, a module-level listener set so
// every mounted useTheme() instance stays in sync without React Context, and
// a `data-theme` attribute on <html> that the CSS theme blocks key off.
//
// `themes.css` (loaded via index.css) defines a CSS variable palette per
// theme + overrides for the Tailwind slate-*/brand-* utility classes that
// hunter uses throughout. Components don't need to change to participate
// in theming — the overrides retarget the classes they already use.

const STORAGE_KEY = 'hunter-theme';
const DEFAULT_THEME = 'light';

// Cross-component subscription set — when any hook calls setTheme, every
// other mounted instance gets notified and re-renders.
const _listeners = new Set();

function _apply(theme) {
  document.documentElement.setAttribute('data-theme', theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // localStorage can fail in private-browsing or quota-exhausted mode.
    // The data-theme attribute still applies for the current session, so
    // ignore the write error and let it retry next time.
  }
  _listeners.forEach((fn) => fn(theme));
}

// 7 themes — same identities as ishareinvest so the operator's mental model
// carries across sister projects. Swatch order: [bg, surface, accent, up].
// Used by the picker to draw the mini-preview tile.
export const THEMES = [
  {
    id: 'dark',
    label: 'Dark',
    swatch: ['#020617', '#0f172a', '#14b8a6', '#22c55e'],
  },
  {
    id: 'light',
    label: 'Light',
    swatch: ['#f0f4f8', '#ffffff', '#0d9488', '#16a34a'],
  },
  {
    id: 'cyberpunk',
    label: 'Cyberpunk',
    swatch: ['#000000', '#0a0a14', '#e040fb', '#00ffd1'],
  },
  {
    id: 'terminal',
    label: 'Terminal',
    swatch: ['#070d07', '#0a150a', '#00e64d', '#00e64d'],
  },
  {
    id: 'luxury',
    label: 'Luxury',
    swatch: ['#07091a', '#0c1228', '#d4af37', '#4ade80'],
  },
  {
    id: 'midnight',
    label: 'Midnight',
    swatch: ['#080514', '#100c22', '#9f7aea', '#48bb78'],
  },
  {
    id: 'highcontrast',
    label: 'High Contrast',
    swatch: ['#000000', '#0d0d0d', '#ffff00', '#00ff00'],
  },
];

// Cheap helper for the early-applier in index.html (mirrored there inline
// to avoid the flash of unstyled content on first paint).
export function readStoredTheme() {
  try {
    return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState(readStoredTheme);

  useEffect(() => {
    const handler = (t) => setThemeState(t);
    _listeners.add(handler);
    return () => _listeners.delete(handler);
  }, []);

  // Re-apply on mount in case the early-applier in index.html ran with a
  // different value (e.g., before localStorage was readable).
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  const setTheme = useCallback((next) => {
    setThemeState(next);
    _apply(next);
  }, []);

  return { theme, setTheme };
}
